/**
 * Writing a template down.
 *
 * A template is a whole `MapDocument`, and a `MapDocument` written out by hand is forty lines of
 * bookkeeping per node — ids, orders, timestamps, an owner — around three words of content. So
 * templates are *authored* as an outline and the document is built from it once, at module load.
 * What ends up on the `Template` is still the document: data, serialisable, and the same shape a
 * KV row will hold when §9's catalogue arrives.
 *
 * Everything here is deterministic. No clock, no random: the same outline produces the same
 * document on every build, which is what lets a test assert against one and what keeps a template
 * from differing between two people's machines. The instance facts — a real map id, a real owner,
 * fresh node ids, a real timestamp — are applied on the way out, in `instantiate.ts`.
 *
 * The one thing this does beyond transcription is **colour**. A template whose branches are all
 * the same grey is a worse map than one somebody made themselves, and colouring every node by
 * hand in every template would be the same decision written forty times. So a top-level branch
 * takes the next colour from the template's palette and its whole subtree inherits it: one rule,
 * one place, and the created map looks the way its preview did.
 */
import { createDocument, createEdge, createNode, orderAtEnd, withContent, } from '@orbit-mind/domain';
import { layoutFor } from '@orbit-mind/layout-engine';
/**
 * The clock every template was written at.
 *
 * A constant rather than `Date.now()`, because a template document that differed between builds
 * would make its own snapshot test meaningless and would give every created map a "last edited"
 * date from whenever the bundle happened to be compiled. `instantiate` replaces it with now.
 */
export const TEMPLATE_EPOCH = 1_700_000_000_000;
/** Who a template's nodes belong to until somebody creates a map from one. */
export const TEMPLATE_AUTHOR = 'template';
export const TEMPLATE_WORKSPACE = 'template';
/** A node that holds content, captioned with its text. */
export function holding(text, content, box) {
    return { text, children: [], content, box };
}
export function node(text, ...children) {
    return {
        text,
        children: children.map((child) => (typeof child === 'string' ? node(child) : child)),
    };
}
/**
 * A node with a glyph on it.
 *
 * For the top-level branches, mostly: a map whose five branches each carry a picture is read by
 * its pictures first, and a leaf with one of its own is a leaf shouting.
 */
export function icon(emoji, text, ...children) {
    return { ...node(text, ...children), emoji };
}
/** A node painted its own way: a highlighted step, a warning, a title card. */
export function styled(style, text, ...children) {
    return { ...node(text, ...children), style };
}
/** Adds a style to a node already written, keeping its glyph and children. */
export function withStyle(style, outline) {
    return { ...outline, style: { ...outline.style, ...style } };
}
/**
 * The colours a template's branches are drawn in.
 *
 * Deliberately a template's own field rather than a reference to the application's accent ramp:
 * a template is data that will one day live in KV, and a colour that resolved differently
 * depending on which client read it would make a preview a promise the created map does not keep.
 */
export const DEFAULT_TEMPLATE_PALETTE = [
    '#4f6ef7',
    '#0ea5a5',
    '#e0771f',
    '#8b5cf6',
    '#d9457c',
    '#3f9a4a',
];
/**
 * Pre-order, because that is the order a person reads an outline in and therefore the order the
 * ids should run in. A template's ids are never seen by a user, but they are seen by whoever
 * reads a failing test.
 */
function plan(root) {
    const planned = [];
    const walk = (outline, parentId, branch, depth) => {
        const id = `n${String(planned.length)}`;
        planned.push({ id, parentId, outline, branch, depth });
        outline.children.forEach((child, childIndex) => {
            // A top-level branch starts a colour; everything below it keeps that colour, which is what
            // makes a branch read as one thing rather than as a row of unrelated boxes.
            walk(child, id, branch ?? childIndex, depth + 1);
        });
    };
    walk(root, null, null, 0);
    return planned;
}
/** Font sizes by depth: a title, chapter headings, and the text under them. */
const SIZE_BY_DEPTH = [18, 15, 13];
function fontSizeFor(depth) {
    return SIZE_BY_DEPTH[Math.min(depth, SIZE_BY_DEPTH.length - 1)] ?? 13;
}
/**
 * How much bigger than its text a shape's box has to be. The same numbers the scene uses, so the
 * boxes a template ships are the boxes the editor would have made.
 */
const PADDING_SCALE = {
    rectangle: { x: 1, y: 1 },
    'rounded-rectangle': { x: 1, y: 1 },
    square: { x: 1, y: 1 },
    circle: { x: 1.6, y: 1.6 },
    ellipse: { x: 1.4, y: 1.4 },
    capsule: { x: 1.3, y: 1 },
    diamond: { x: 3.2, y: 2.6 },
    hexagon: { x: 2, y: 1 },
    octagon: { x: 1.4, y: 1.2 },
    cloud: { x: 2.4, y: 2 },
    card: { x: 1, y: 1 },
    'sticky-note': { x: 1, y: 1 },
    balloon: { x: 1.2, y: 1.3 },
    'invisible-frame': { x: 1, y: 1 },
};
/**
 * Roughly how big a node's box will be.
 *
 * The layout needs a size per node, and measuring text is the renderer's job — a package that
 * ships templates has no font and no canvas. So this estimates with the same rules the scene
 * uses: wrap at `MAX_TEXT_WIDTH`, about 0.6 of the font size per character, the scene's padding
 * scaled for the shape, and its minimums.
 */
function estimateExtent(text, fontSize = 14, shape = 'rounded-rectangle') {
    const GLYPH_RATIO = 0.6;
    const MAX_TEXT_WIDTH = 220;
    const PADDING = { x: 12, y: 8 };
    const LINE_HEIGHT = fontSize * 1.35;
    const scale = PADDING_SCALE[shape];
    const wanted = Math.max(1, text.length) * fontSize * GLYPH_RATIO;
    const textWidth = Math.min(MAX_TEXT_WIDTH, wanted);
    const lines = Math.max(1, Math.ceil(wanted / MAX_TEXT_WIDTH));
    return {
        width: Math.max(40, Math.round(textWidth + PADDING.x * 2 * scale.x)),
        height: Math.max(28, Math.round(lines * LINE_HEIGHT + PADDING.y * 2 * scale.y)),
    };
}
export function documentFor(input) {
    const mapId = `template-${input.templateId}`;
    const palette = input.palette ?? DEFAULT_TEMPLATE_PALETTE;
    const planned = plan(input.root);
    const first = planned[0];
    if (first === undefined)
        throw new Error('a template needs at least a root node');
    const base = createDocument({
        mapId,
        rootNodeId: first.id,
        workspaceId: TEMPLATE_WORKSPACE,
        ownerId: TEMPLATE_AUTHOR,
        now: TEMPLATE_EPOCH,
        title: first.outline.text,
        rootText: first.outline.text,
        ...(input.layoutId === undefined ? {} : { layoutId: input.layoutId }),
    });
    // The first node carrying a text, for content and links that refer to another node.
    const idOf = (text) => {
        const found = planned.find((entry) => entry.outline.text === text);
        if (found === undefined)
            throw new Error(`template ${input.templateId}: no node says "${text}"`);
        return found.id;
    };
    const nodes = {};
    // Orders are appended sibling by sibling with the domain's own key generator, so the keys a
    // template ships are keys the editor would have produced — inserting between two of them works
    // on the first try rather than after a re-key nobody expected.
    const ordersByParent = new Map();
    for (const entry of planned) {
        const siblings = ordersByParent.get(entry.parentId) ?? [];
        const order = orderAtEnd(siblings);
        ordersByParent.set(entry.parentId, [...siblings, order]);
        const created = createNode({
            id: entry.id,
            mapId,
            parentId: entry.parentId,
            order,
            now: TEMPLATE_EPOCH,
            by: TEMPLATE_AUTHOR,
            text: entry.outline.text,
        });
        const shape = shapeFor(entry, input);
        const content = entry.outline.content;
        const metadata = content === undefined
            ? undefined
            : withContent(undefined, typeof content === 'function' ? content(idOf) : content);
        const box = entry.outline.box;
        nodes[entry.id] = {
            ...created,
            ...(entry.outline.emoji === undefined ? {} : { emoji: entry.outline.emoji }),
            style: styleFor(entry, palette, input),
            ...(shape === undefined ? {} : { shape }),
            ...(metadata === undefined ? {} : { metadata }),
            ...(box === undefined ? {} : { width: box.width, height: box.height }),
        };
    }
    const edges = {};
    (input.links ?? []).forEach((link, index) => {
        const id = `e${String(index)}`;
        edges[id] = createEdge({
            id,
            mapId,
            sourceNodeId: idOf(link.from),
            targetNodeId: idOf(link.to),
            now: TEMPLATE_EPOCH,
            ...(link.label === undefined ? {} : { label: link.label }),
        });
    });
    const document = {
        map: input.themeId === undefined ? base.map : { ...base.map, themeId: input.themeId },
        nodes,
        edges,
    };
    return arranged(document);
}
function shapeFor(entry, input) {
    if (entry.outline.style?.shape !== undefined)
        return entry.outline.style.shape;
    if (entry.depth === 0)
        return input.rootShape;
    if (entry.depth === 1)
        return input.branchShape ?? input.shape;
    return input.shape;
}
/**
 * The template, laid out by its own layout.
 *
 * A template that shipped without positions would be a pile of boxes at the origin until
 * something arranged it — which is what every preview of one showed, and what the first frame
 * after creating one looked like. Running the layout here means a template *is* a map.
 */
function arranged(document) {
    // A node that holds content was given its box; every other one is estimated from its words.
    const extents = new Map(Object.values(document.nodes).map((node) => [
        node.id,
        node.width > 0 && node.height > 0
            ? { width: node.width, height: node.height }
            : estimateExtent(node.plainText, node.style.fontSize, node.shape),
    ]));
    const { positions } = layoutFor(document.map.layoutId).run({
        document,
        extentOf: (nodeId) => extents.get(nodeId) ?? { width: 40, height: 28 },
        // Nothing here was placed by hand: every node in a template is where the layout puts it.
        preserveManualPositions: false,
    });
    const nodes = Object.fromEntries(Object.values(document.nodes).map((node) => {
        const placed = positions[node.id];
        const estimate = extents.get(node.id) ?? estimateExtent(node.plainText, node.style.fontSize, node.shape);
        return [
            node.id,
            {
                ...node,
                // A layout returns only the nodes it *moved*, so a node already where it belongs — the
                // root, usually — is absent and keeps the position it has.
                ...(placed === undefined ? {} : { x: placed.x, y: placed.y }),
                // The size is set for every node either way. The layout may size one as well as place
                // it; when it does not, the estimate it was given is the honest size to ship, and a
                // box of zero by zero is a node no renderer can draw.
                width: placed?.width ?? estimate.width,
                height: placed?.height ?? estimate.height,
            },
        ];
    }));
    return { ...document, nodes };
}
/** Relative luminance, enough to decide between dark and light text on a fill. */
function isDarkColour(colour) {
    const value = colour.replace('#', '');
    if (value.length !== 6)
        return false;
    const channel = (offset) => {
        const c = parseInt(value.slice(offset, offset + 2), 16) / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    };
    const luminance = 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
    return luminance < 0.4;
}
/** The text colour that reads on `fill`. */
export function inkOn(fill) {
    return (isDarkColour(fill) ? '#ffffff' : '#1f2937');
}
/** `colour` mixed towards `target` by `amount` (0 keeps it, 1 is the target). */
export function mix(colour, target, amount) {
    const parse = (hex) => {
        const value = hex.replace('#', '');
        return [0, 2, 4].map((offset) => parseInt(value.slice(offset, offset + 2), 16));
    };
    const a = parse(colour);
    const b = parse(target);
    const out = a.map((channel, index) => Math.round(channel + ((b[index] ?? channel) - channel) * amount)
        .toString(16)
        .padStart(2, '0'));
    return `#${out.join('')}`;
}
/**
 * How a node is painted.
 *
 * The root is filled and its text reversed out, because it is the one node that is about the
 * whole map rather than about a part of it. Everything else follows the template's look, and a
 * node's own `style` wins over both.
 */
function styleFor(entry, palette, input) {
    const own = entry.outline.style;
    const fontSize = own?.size ?? fontSizeFor(entry.depth);
    const surface = input.surface;
    const ink = input.ink;
    let base;
    if (entry.branch === null) {
        const accent = palette[0] ?? DEFAULT_TEMPLATE_PALETTE[0] ?? '#4f6ef7';
        const fill = input.rootStyle?.fill ?? accent;
        base = {
            fill,
            stroke: input.rootStyle?.stroke ?? fill,
            textColor: input.rootStyle?.text ?? inkOn(fill),
            fontWeight: 700,
            fontSize: input.rootStyle?.size ?? fontSize,
        };
    }
    else {
        const colour = palette[entry.branch % palette.length] ?? '#4f6ef7';
        const look = input.look ?? 'outline';
        const paper = surface ?? '#ffffff';
        const solidFill = look === 'solid' || (look === 'filled' && entry.depth === 1)
            ? entry.depth === 1
                ? colour
                : mix(colour, paper, Math.min(0.75, 0.45 + 0.15 * (entry.depth - 2)))
            : undefined;
        if (look === 'text') {
            base = { stroke: colour, textColor: colour, strokeWidth: 1.5 };
        }
        else if (solidFill !== undefined) {
            base = { fill: solidFill, stroke: colour, textColor: inkOn(solidFill), strokeWidth: 1.5 };
        }
        else {
            base = {
                stroke: colour,
                strokeWidth: 1.5,
                ...(surface === undefined ? {} : { fill: surface }),
                ...(ink === undefined ? {} : { textColor: ink }),
            };
        }
        base.fontSize = fontSize;
        if (entry.depth === 1)
            base.fontWeight = 600;
    }
    if (own !== undefined) {
        if (own.fill !== undefined) {
            base.fill = own.fill;
            base.textColor = own.text ?? inkOn(own.fill);
        }
        if (own.text !== undefined)
            base.textColor = own.text;
        if (own.stroke !== undefined)
            base.stroke = own.stroke;
        if (own.bold !== undefined)
            base.fontWeight = own.bold ? 700 : 400;
        if (own.italic === true)
            base.italic = true;
        if (own.dashed === true)
            base.strokeDash = [5, 4];
    }
    return base;
}
//# sourceMappingURL=outline.js.map